import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import type { Table } from "@tanstack/react-table";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { isUndefined } from "@/utils/typeGuards";

import Icon from "../../Icon/Icon";

// DataTableColumnHeaderと同じ正規表現を使用
const DISPLAY_LABEL_REGEX = /^(.+?)\[.+\]$/;

/**
 * 表示ラベルから表示テキストを抽出（DataTableColumnHeaderと同じロジック）
 * @param displayLabel "外部MDM ID[externalMdmId]" または undefined
 * @returns "外部MDM ID" または元の値
 */
function extractDisplayText(displayLabel?: string): string {
  if (!displayLabel || displayLabel.trim() === "") {
    return "";
  }

  const match = displayLabel.match(DISPLAY_LABEL_REGEX);
  return match && match[1] ? match[1].trim() : displayLabel;
}

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
}

export function DataTableViewOptions<TData>({
  table,
}: DataTableViewOptionsProps<TData>) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.dataTable.viewOptions",
  });
  const { t: tHeader } = useTranslation(defaultNS, {
    keyPrefix: "component.dataTable.headers",
  });

  const columns = table.getAllColumns();
  const selectableColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          !isUndefined(column.accessorFn) && // has valid accessor key or function
          column.getCanHide() && // can toggle visibility
          column.id !== "actions", // exclude actions column
      ),
    [columns],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Icon iconName="SlidersHorizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("toggleColumns")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {selectableColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.getIsVisible()}
            onCheckedChange={(value) => column.toggleVisibility(value)}
            className="cursor-pointer"
          >
            {column.columnDef.meta?.i18nKey
              ? tHeader(column.columnDef.meta.i18nKey)
              : extractDisplayText(column.columnDef.meta?.displayLabel) ||
                column.id}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
